import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'

import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import SearchInput from '@/modules/drivers/shared/SearchInput'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableRadio from '@/modules/drivers/shared/tableItems/TableRadio'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { FaIcon, faClose } from '@/lib/fa'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { invalidateAfterImport } from '@/modules/imports/utils/invalidateAfterImport'
import {
  useMutationAssignDriverCard,
  useQueryDrivers,
} from '@/lib/api/company/drivers'
import type { Driver } from '@/lib/api/company/drivers/types'

type Props = {
  companyId: string
  driverId: number
  driverCardId: number
  close: () => void
  onAssigned?: () => void
}

const AssignCardToDriverModal = ({
  companyId,
  driverId,
  driverCardId,
  close,
  onAssigned,
}: Props) => {
  const { t } = useLocalize()
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const { data, isLoading } = useQueryDrivers({ companyId })
  const { mutateAsync, isPending } = useMutationAssignDriverCard(companyId)

  const [search, setSearch] = React.useState('')
  const [selectedDriverId, setSelectedDriverId] = React.useState<number | null>(
    null,
  )

  const columns: TableHeaderColumn[] = [
    {
      title: t('assignDriversPopup.surname'),
      key: 'surname',
      type: 'string',
      sort: true,
    },
    {
      title: t('assignDriversPopup.name'),
      key: 'name',
      type: 'string',
      sort: true,
    },
    {
      title: t('assignDriversPopup.cardNumber'),
      key: 'driverCard.cardNumber.value',
      type: 'string',
      sort: true,
    },
  ]

  const drivers = React.useMemo(() => {
    const all: Driver[] = Array.isArray(data) ? data : (data?.drivers ?? [])
    const phrase = search.trim().toLowerCase()
    return all
      .filter((d) => d.id !== driverId)
      .filter(
        (d) =>
          !phrase ||
          [d.surname, d.name, d.driverCard?.cardNumber?.value].some((v) =>
            v?.toLowerCase().includes(phrase),
          ),
      )
      .sort(
        (a, b) =>
          (a.surname ?? '').localeCompare(b.surname ?? '') ||
          (a.name ?? '').localeCompare(b.name ?? ''),
      )
  }, [data, search, driverId])

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: drivers,
    tableColumns: columns,
    includeActiveSorting: false,
  })

  const assign = async (targetDriverId: number | null) => {
    if (!targetDriverId || isPending) return

    const ok = await confirm({
      title: t('common.warning'),
      message: t('driverModule.assign-card-confirm'),
      confirmText: t('answer.yes'),
      cancelText: t('answer.no'),
    })
    if (!ok) {
      close()
      return
    }

    try {
      await mutateAsync({ driverId, driverCardId, targetDriverId })
      invalidateAfterImport(queryClient)
      toast.success({ text: t('driverModule.assign-card-success') })
      onAssigned?.()
      close()
    } catch {
      toast.error({ text: t('driverModule.assign-card-failed') })
    }
  }

  return (
    <ModalLayout customZIndex={250}>
      <div
        className="tw-relative tw-flex tw-max-h-[80vh] tw-w-full tw-max-w-[640px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {(isLoading || isPending) && (
          <div className="tw-absolute tw-inset-0 tw-z-[60] tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-black tw-bg-opacity-50">
            <div className="tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-gray-200 tw-border-t-primary"></div>
          </div>
        )}

        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-6 tw-py-4">
          <p className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content-base">
            {t('assignDriversPopup.title')}
          </p>
          <FaIcon
            icon={faClose}
            className="tw-h-5 tw-w-5 tw-cursor-pointer"
            onClick={close}
          />
        </div>

        <div className="tw-px-4 tw-pt-2">
          <SearchInput
            inline
            id="assign-card-driver-search"
            search={search}
            setSearch={setSearch}
            searchResults={sortedData.length}
            placeholder={t('common.search')}
          />
        </div>

        <div className="tw-mx-6 tw-mb-4 tw-min-h-[200px] tw-flex-1 tw-overflow-auto tw-rounded-lg tw-border tw-border-base-300">
          <table className="tw-w-full tw-table-auto tw-border-collapse">
            <TableHeader
              columns={columns}
              fontSize="md"
              bolder
              sticky
              disabledToggle
              sortConfig={sortConfig}
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {sortedData.map((driver) => {
                const isChecked = driver.id === selectedDriverId
                return (
                  <tr
                    key={driver.id}
                    className={`tw-border-b hover:tw-bg-primary/10 ${isChecked ? 'selected tw-bg-primary/10' : 'tw-bg-white'}`}
                    onClick={() => setSelectedDriverId(driver.id)}
                    onDoubleClick={() => assign(driver.id)}
                  >
                    <TableRadio
                      background
                      disabled={false}
                      checked={isChecked}
                      onClick={() => setSelectedDriverId(driver.id)}
                    />
                    <TableTextRow size="small">{driver.surname}</TableTextRow>
                    <TableTextRow size="small">{driver.name}</TableTextRow>
                    <TableTextRow size="small">
                      {driver.driverCard?.cardNumber?.value || '-'}
                    </TableTextRow>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {!isLoading && sortedData.length === 0 && (
            <p className="tw-py-8 tw-text-center tw-text-sm tw-text-content-base tw-opacity-70">
              {t('common.no-results')}
            </p>
          )}
        </div>

        <div className="tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-base-300 tw-px-6 tw-py-4">
          <button
            type="button"
            onClick={close}
            className="tw-rounded-lg tw-border tw-border-base-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-200"
          >
            {t('answer.cancel')}
          </button>
          <button
            type="button"
            disabled={!selectedDriverId || isPending}
            onClick={() => assign(selectedDriverId)}
            className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
          >
            {t('assignActivitiesToDriverPopup.assign')}
          </button>
        </div>
      </div>
    </ModalLayout>
  )
}

export default AssignCardToDriverModal
